import React, { FC, useEffect, useState } from "react";
import { Formik, FormikHelpers, FormikValues, Form } from "formik";
import { Button, Stack, Typography } from "@mui/material";
import * as yup from "yup";
import toast from "react-hot-toast";
import { FieldText } from "../../../../components/fields/Text/Text";
import axiosInstant from "../../../../api";
import { updateVideoSettingsEndpoint } from "../../../../api/endpoints";
import { AntSwitch } from "./AntSwitchDesign";
import { formatVideoName } from "../hooks/useFetchVideos";
import { PanelFooter } from "./PanelFooter";

const videoSettingsSchema = yup.object().shape({
  name: yup.string().required(),
});

interface Props {
  brandId: number;
  video: any;
  onComplete: (data: any) => void;
}

export const SettingsForm: FC<Props> = ({
  brandId,
  video,
  onComplete,
}) => {
  const [publishedValue, setPublishedValue] = useState(video?.published_public ?? false);
  // Demo flag: marks the video as a proposal example/use-case (shown on the
  // public "Choose Your Path" proposal pages + PDF for its product category).
  const [demoValue, setDemoValue] = useState(video?.demo ?? false);

  const getInitialValues = () => ({
    //name: formatVideoName(String(video?.key || video?.name || "")),
    name: formatVideoName(String(video?.name || video?.key || "")),
    published_public: video?.published_public ?? false,
  });

  const [initialValues, setInitValues] = useState(getInitialValues);

  const handleSubmit = async (
    values: FormikValues,
    { setSubmitting }: FormikHelpers<typeof initialValues>
  ) => {
    const payload = {
      name: values.name,
      brandId,
      published: publishedValue,
      demo: demoValue,
    };
    const updateVideoUrl = updateVideoSettingsEndpoint(video.id);
    console.log("Payload for updating video settings:", payload);
    console.log("Update video settings URL:", updateVideoUrl);
    if (updateVideoUrl) {
      try {
        const response = await axiosInstant.patch(updateVideoUrl, payload);
        const { status } = response;
        if (status === 200) {
          toast.success(`Video ${values.name} settings has been updated.`);
          onComplete(payload);
        } else toast.error(`Some error encountered.`);
      } catch (error) {
        toast.error(`Some error encountered.`);
      }
    }

    setSubmitting(false);
  };

  const handlePublishedChange = async (e: any) => {
    setPublishedValue(e.target.checked);
  };

  useEffect(() => {
    if (video) {
      const newValues = getInitialValues();
      setInitValues(newValues);
      setPublishedValue(video?.published_public ?? false);
      setDemoValue(video?.demo ?? false);
    }
  }, [video?.id, video?.name, video?.published_public, video?.demo]);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={videoSettingsSchema}
      onSubmit={handleSubmit}
    >
      <Form className="flex flex-col justify-between h-full">
        <Stack width={1} spacing={4} className="flex-1 min-h-0 overflow-y-auto">
          <Stack width={1} spacing={1}>
            <FieldText
              name="name"
              label="Video name"
              size="small"
              placeholder="Video Name"
              className="border-0 rounded-xl bg-surface-2"
              style={{ width: 350 }}
            />
          </Stack>
          <Stack direction="row" spacing={10} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <AntSwitch
                checked={publishedValue}
                inputProps={{ "aria-label": "ant design" }}
                onChange={handlePublishedChange}
              />
              <Typography>Published</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <AntSwitch
                checked={demoValue}
                inputProps={{ "aria-label": "demo example" }}
                onChange={(e: any) => setDemoValue(e.target.checked)}
              />
              <Typography>Demo example</Typography>
            </Stack>
          </Stack>
        </Stack>
        <PanelFooter>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
            sx={{ textTransform: "capitalize" }}
          >
            Save Changes
          </Button>
        </PanelFooter>
      </Form>
    </Formik>
  );
};
